import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import api from "../constant/api";
import { 
  LayoutDashboard, Package, ShoppingCart, Users, Settings, 
  FileSignature, FileSpreadsheet, Warehouse, FileCheck, 
  Truck, Undo2, FileText, BadgePercent, HelpCircle, X
} from "lucide-react";

const ICON_MAPPING = {
  "LayoutDashboard": LayoutDashboard,
  "Package": Package,
  "ShoppingCart": ShoppingCart,
  "Users": Users,
  "Settings": Settings,
  "FileSignature": FileSignature,
  "FileSpreadsheet": FileSpreadsheet,
  "Warehouse": Warehouse,
  "FileCheck": FileCheck,
  "Truck": Truck,
  "Undo2": Undo2,
  "FileText": FileText,
  "BadgePercent": BadgePercent,
  "HelpCircle": HelpCircle
};

export default function Sidebar({ isOpen, setIsOpen }) {
  const [dynamicGroups, setDynamicGroups] = useState([]);
  
  // 🎯 1. ALLOWED MENU IDS FROM LIVE MATRIX (We will store allowed menu IDs here)
  const [allowedMenuIds, setAllowedMenuIds] = useState([]);
  const userEmail = localStorage.getItem("userEmail"); // Logged in admin email tracking

  const loadMenuManagementSettings = async () => {
    try {
      // A. Fetch Allowed Menus History first using Login Email ID
      if (userEmail) {
        const allowedRes = await api.get(`/api/admin/get-allowed-menus?email=${userEmail}`);
        if (allowedRes.data && allowedRes.data.success) {
          
          // 💡 மாஸ் லாஜிக்: ஏபிஐ-ல் இருந்து வரும் ["1_view", "2_edit"] இல் இருந்து 
          // 'view' அனுமதி உள்ள மெனு ஐடிகளை மட்டும் பிரித்தெடுத்து எண்களாக (Numbers) சேமிக்கிறோம்.
          const allowedIds = allowedRes.data.data
            .filter(permission => permission.endsWith("_view")) // view அனுமதி இருப்பதை மட்டும் எடுக்கும்
            .map(permission => Number(permission.split("_")[0])); // "1_view" -> 1 என மாற்றும்

          setAllowedMenuIds(allowedIds);
        }
      }

      // B. Fetch All System Master Menus structure sequence
      const res = await api.get("/menus/getMenus");
      if (res.data && res.data.success) {
        const responseData = res.data.data;

        if (Array.isArray(responseData) && responseData.length > 0 && responseData[0].groupName) {
          setDynamicGroups(responseData);
        } else if (Array.isArray(responseData)) {
          const groupsMap = {};
          responseData.forEach((row) => {
            const groupKey = row.group_name || "General Modules";
            if (!groupsMap[groupKey]) {
              groupsMap[groupKey] = { groupName: groupKey, items: [] };
            }
            groupsMap[groupKey].items.push({
              id: row.id, // Database Primary Key ID
              title: row.title,
              to: row.path,
              icon: row.icon_name
            });
          });
          setDynamicGroups(Object.values(groupsMap));
        }
      }
    } catch (err) {
      console.error("Error loading layout menus:", err);
    }
  };

  useEffect(() => {
    loadMenuManagementSettings();
    window.addEventListener("settingsSaved", loadMenuManagementSettings);
    return () => window.removeEventListener("settingsSaved", loadMenuManagementSettings);
  }, [userEmail]);

  return (
    <>
      {/* 1. MOBILE BACKDROP GLASS OVERLAY */}
      {isOpen && (
        <div 
          onClick={() => setIsOpen(false)}
          className="fixed inset-0 z-40 bg-slate-950/60 backdrop-blur-sm md:hidden transition-opacity duration-300"
        />
      )}

      {/* 2. MAIN 3D SIDEBAR CONTAINER */}
      <div
        className={`fixed z-50 top-0 left-0 h-full w-66 bg-slate-950 text-slate-100 p-5 flex flex-col transform 
        ${isOpen ? "translate-x-0" : "-translate-x-full"} 
        md:translate-x-0 transition-all duration-300 border-r border-slate-800/50 shadow-[5px_0_25px_rgba(0,0,0,0.6)]`}
      >
        {/* BRAND HEADER & MOBILE CLOSE TRIGGER BUTTON */}
        <div className="mb-8 pb-5 border-b border-slate-800/60 shrink-0 flex items-center justify-between px-2">
          <div>
            <h2 className="text-2xl font-black bg-gradient-to-r from-blue-400 via-indigo-400 to-cyan-400 bg-clip-text text-transparent drop-shadow-[0_2px_10px_rgba(56,189,248,0.3)]">
              AK ADMIN
            </h2>
            <p className="text-[10px] font-bold text-cyan-400/80 uppercase tracking-widest mt-0.5">
              ✦ Next-Gen System
            </p>
          </div>
          <button 
            onClick={() => setIsOpen(false)} 
            className="md:hidden p-1.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-400 hover:text-white"
          >
            <X size={18} />
          </button>
        </div>

        {/* RENDER GROUPS & LINKS WITH INLINE 3D VIEW */}
        <div className="flex-1 overflow-y-auto pr-1 space-y-7 custom-scrollbar">
          {dynamicGroups.map((group, groupIdx) => {
            
            // 🎯 2. DYNAMIC HISTORICAL MENUS FILTER MATRIX
            // செக் பாக்ஸ் மேட்ரிக்ஸ் மூலம் அனுமதிக்கப்பட்ட ஐடிகள் மட்டும் பில்டர் செய்யப்படுகிறது
            const visibleItems = group.items ? group.items.filter(item => allowedMenuIds.includes(Number(item.id))) : [];

            // குரூப்பில் எந்த மெனுவும் இல்லை என்றால் குரூப் பெயரைக் காட்ட வேண்டாம்
            if (visibleItems.length === 0) return null;

            return (
              <div key={groupIdx} className="space-y-3">
                <p className="text-[12px] font-extrabold text-slate-400/90 uppercase tracking-wider px-3 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-500/80 shadow-[0_0_6px_rgba(99,102,241,0.6)]" />
                  {group.groupName}
                </p>
                
                <ul className="space-y-2">
                  {visibleItems.map((item, itemIdx) => {
                    const targetIcon = item.icon || item.icon_name;
                    const IconComponent = ICON_MAPPING[targetIcon] || HelpCircle;
                    
                    return (
                      <li key={itemIdx} className="px-1">
                        <NavLink
                          to={item.to}
                          onClick={() => setIsOpen(false)}
                          className={({ isActive }) =>
                            `flex items-center gap-3.5 px-4 py-3 rounded-xl text-xs font-semibold transition-all duration-200 border transform active:scale-95 ${
                              isActive
                                ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-blue-400/30 -translate-y-[2px]"
                                : "text-slate-400 bg-slate-900/40 border-slate-800/40 hover:text-white hover:bg-slate-800/50 hover:border-slate-700/60 hover:-translate-y-[1px]"
                            }`
                          }
                          style={({ isActive }) => isActive ? {
                            boxShadow: `
                              0 4px 6px -1px rgba(0, 0, 0, 0.2), 
                              0 10px 20px -5px rgba(59, 130, 246, 0.45), 
                              inset 0 1px 1px rgba(255, 255, 255, 0.35)
                            `
                          } : {
                            boxShadow: "0 2px 4px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                          }}
                        >
                          {({ isActive }) => (
                            <>
                              <div className={`p-1.5 rounded-lg shrink-0 transition-all ${
                                isActive ? "bg-white/10 text-white" : "bg-slate-950/60 text-slate-400 group-hover:text-blue-400"
                              }`}>
                                <IconComponent 
                                  size={16} 
                                  className={isActive ? "drop-shadow-[0_0_5px_rgba(255,255,255,0.8)]" : ""}
                                />
                              </div>
                              
                              <span className={`tracking-wide transition-all text-[13px] ${isActive ? "text-white font-bold" : "text-slate-300"}`}>
                                {item.title}
                              </span>

                              {isActive && (
                                <span className="ml-auto w-1.5 h-1.5 bg-cyan-300 rounded-full animate-pulse shadow-[0_0_8px_#22d3ee]" />
                              )}
                            </>
                          )}
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}